import { normalizeEventTypeKey } from '../eventTypes';
import { isOnlineEventFormat, normalizeEventFormat } from './eventFormatConstants';
import { formatAttendeeEligibilityForApi } from '../../../shared/registrationSettings';
import { formatTopicsForApi } from './eventTopicsConstants';
import { MENU_COURSES } from './manageEventForm';
import {
  COURSE_API_KEYS,
  COVER_IMAGE_FIELD,
  MENU_ATTACH_FILE_FIELD,
  buildMenuDishImageFieldName,
  normalizeMultilineText,
} from './eventFormConstants';

export const formatDateForApi = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Converts UI time (e.g. "6:00 PM") or 24h ("18:00") to API format "HH:mm". */
export const formatTimeForApi = (time) => {
  if (!time) return '';
  const trimmed = String(time).trim();

  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed) && !/am|pm/i.test(trimmed)) {
    const [hours, minutes] = trimmed.split(':');
    return `${String(hours).padStart(2, '0')}:${minutes.slice(0, 2)}`;
  }

  const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2] ?? '00';
  const period = match[3].toUpperCase();

  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  return `${String(hours).padStart(2, '0')}:${minutes}`;
};

const buildMenuItemJson = (dish) => {
  const item = { dishName: dish.dishName.trim() };
  const description = normalizeMultilineText(dish.description)?.trim();
  if (description) item.description = description;
  return item;
};

/**
 * Builds menuItems JSON and file field names aligned by course index:
 * menuImage_starter_0, menuImage_starter_1, menuImage_main_0, …
 */
export const buildMenuPayload = (menuSections = []) => {
  const menuItems = { starter: [], main: [], desert: [] };
  const menuImageFiles = [];

  MENU_COURSES.forEach((courseLabel) => {
    const apiKey = COURSE_API_KEYS[courseLabel];
    const section = menuSections.find((s) => s.course === courseLabel);
    const dishes = section?.dishes ?? [];

    dishes.forEach((dish) => {
      const name = dish.dishName?.trim();
      if (!name) return;

      const dishIndex = menuItems[apiKey].length;
      menuItems[apiKey].push(buildMenuItemJson({ ...dish, dishName: name }));

      if (dish.imageFile instanceof File) {
        menuImageFiles.push({
          fieldName: buildMenuDishImageFieldName(apiKey, dishIndex),
          file: dish.imageFile,
        });
      }
    });
  });

  return { menuItems, menuImageFiles };
};

/**
 * Maps admin form state to multipart FormData for create/update event APIs.
 * @param {{ published?: boolean }} [options] — when `published` is set, appends the published field (create only).
 */
export const buildEventFormData = (form, { published, includePartnerSettings = false } = {}) => {
  const formData = new FormData();
  const { menuItems, menuImageFiles } = buildMenuPayload(form.menuSections);

  formData.append('title', form.title?.trim() ?? '');
  formData.append('type', normalizeEventTypeKey(form.type));
  formData.append('totalSeats', String(form.totalSeats ?? ''));
  formData.append('price', String(form.price ?? ''));
  formData.append('currency', form.currency ?? 'AED');
  formData.append('description', form.description?.trim() ?? '');
  formData.append('wingmanDescription', form.wingmanDescription?.trim() ?? '');
  formData.append('topics', JSON.stringify(formatTopicsForApi(form.topics)));
  formData.append('requireJoinApproval', form.requireJoinApproval ? 'true' : 'false');
  formData.append('attendeeEligibility', formatAttendeeEligibilityForApi(form.attendeeEligibility));
  formData.append('memberMatching', form.memberMatching ? 'true' : 'false');
  // formData.append('bookingDeadline', formatDateForApi(form.bookingDeadline));
  formData.append('eventDate', formatDateForApi(form.eventDate));
  formData.append('startTime', formatTimeForApi(form.startTime));
  formData.append('endTime', formatTimeForApi(form.endTime));
  const eventFormat = normalizeEventFormat(form.eventFormat);
  const isOnline = isOnlineEventFormat(eventFormat);

  formData.append('eventFormat', eventFormat);
  formData.append('onlineMeetingLink', isOnline ? (form.onlineMeetingLink?.trim() ?? '') : '');
  formData.append('venueName', isOnline ? '' : (form.venueName?.trim() ?? ''));
  formData.append('location', isOnline ? '' : (form.location?.trim() ?? ''));
  formData.append('latitude', isOnline ? '' : String(form.latitude ?? ''));
  formData.append('longitude', isOnline ? '' : String(form.longitude ?? ''));
  formData.append('menuItems', JSON.stringify(menuItems));

  if (published !== undefined) {
    formData.append('published', published ? 'true' : 'false');
  }

  if (includePartnerSettings) {
    appendPartnerSettingsFields(formData, form);
  }

  if (form.coverImageFile instanceof File) {
    formData.append(COVER_IMAGE_FIELD, form.coverImageFile);
  }

  menuImageFiles.forEach(({ fieldName, file }) => {
    formData.append(fieldName, file);
  });

  if (form.menuAttachFile instanceof File) {
    formData.append(MENU_ATTACH_FILE_FIELD, form.menuAttachFile);
  }

  return formData;
};

/** POST /admin/add-event */
export const buildAddEventFormData = (form, { published = false } = {}) => {
  const formData = buildEventFormData(form, { published });
  formData.append('enableWaitlist', form.enableWaitlist ? 'true' : 'false');
  return formData;
};

/** PUT /admin/edit-event/:id */
export const buildEditEventFormData = (form, options = {}) => buildEventFormData(form, options);

const appendPartnerSettingsFields = (formData, form) => {
  formData.append('enableWaitlist', form.enableWaitlist ? 'true' : 'false');
  formData.append('inviteOnly', form.inviteOnly ? 'true' : 'false');
  formData.append('refundPolicy', form.refundPolicy ?? '');
};

/**
 * Partner create/update — same fields as admin + settings + required saveAs (draft | submit).
 * @param {'draft'|'submit'} saveAs
 */
export const buildPartnerEventFormData = (form, { saveAs }) => {
  const formData = buildEventFormData(form);
  appendPartnerSettingsFields(formData, form);
  formData.append('saveAs', saveAs);
  return formData;
};
